import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History, Save } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button, buttonVariants } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useCapabilitiesStore } from '../../stores/capabilitiesStore';
import { Permission } from '../../auth/permissions';

interface Setting {
  key: string;
  value: string;
  value_type: string;
  category: string;
  description: string | null;
  updated_at: string;
}

/** Screen 43 — Admin Settings (20+ params). OWN/ADM only. ADM sees infra; OWN/MGR see business+doc-format. */
export default function Settings() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canViewAudit = useCapabilitiesStore((s) => s.permissionSet).has(Permission.AUDIT_VIEW);
  const [rows, setRows] = useState<Setting[]>([]);
  const [dirty, setDirty] = useState<Record<string, string>>({});

  useEffect(() => {
    apiClient
      .get<Setting[]>('/admin/settings')
      .then((r) => setRows(r.data))
      .catch(showApiError);
  }, [showApiError]);

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    try {
      await apiClient.put('/admin/settings', dirty, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({ tone: 'success', message: t('admin.settings.saved') });
      setDirty({});
      const { data } = await apiClient.get<Setting[]>('/admin/settings');
      setRows(data);
    } catch (err) {
      showApiError(err);
    }
  };

  const groups = rows.reduce<Record<string, Setting[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div>
      <PageHeader
        title={t('admin.settings.title')}
        description={t('admin.settings.description')}
        actions={
          <div className="flex items-center gap-2">
            {canViewAudit && (
              <Link
                to="/admin/audit?entity=admin_settings"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <History className="h-4 w-4" />
                {t('admin.settings.changeHistory')}
              </Link>
            )}
            <Button variant="success" size="sm" disabled={dirtyCount === 0} onClick={save}>
              <Save className="h-4 w-4" />
              {t('common.save')}
              {dirtyCount ? ` (${dirtyCount})` : ''}
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {Object.entries(groups).map(([cat, items]) => (
          <section key={cat} className="border rounded-lg bg-card shadow-sm overflow-hidden">
            <header className="px-4 py-2 border-b text-xs uppercase tracking-wide text-muted-foreground">
              {cat.replace('_', ' ')}
            </header>
            <table className="w-full text-sm">
              <tbody>
                {items.map((s) => (
                  <tr key={s.key} className="border-t">
                    <td className="py-1.5 px-4 align-top w-1/3">
                      <div className="font-medium truncate max-w-[18rem]" title={s.key}>
                        {s.key}
                      </div>
                      <div
                        className="text-xs text-muted-foreground truncate max-w-[18rem]"
                        title={s.description ?? undefined}
                      >
                        {s.description}
                      </div>
                    </td>
                    <td className="py-1.5 px-4">
                      <div className="flex items-center gap-2">
                        <Input
                          className="h-8 w-48"
                          value={dirty[s.key] ?? s.value}
                          onChange={(e) => setDirty({ ...dirty, [s.key]: e.target.value })}
                        />
                        <span className="text-xs text-muted-foreground/60">{s.value_type}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}
